/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  HumanErrorDamageType,
  OosRequestType,
  OosTicketMaintenanceType,
  OpportunityMXType,
  OutOfServiceType,
  RtsDelayReasonType,
} from 'src/graphql/generated/graphql.generated';
import {
  IdListItemRecord,
  IdListItemProps,
} from 'src/store/generated/id-list-item-record.generated';
import { AddressRecord, AddressProps } from 'src/store/generated/address-record.generated';
import {
  OutOfServiceCrewRecord,
  OutOfServiceCrewProps,
} from 'src/store/generated/out-of-service-crew-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface OutOfServiceEventProps {
  id: string;
  type: OutOfServiceType;
  eventNumber: string;
  estimatedEndTime: Date;
  startTime: Date | null;
  isOpportunisticOOS: boolean | null;
  unitId: string | null;
  vehicleId: string | null;
  reasonId: string;
  isAirworthy: boolean;
  secondaryReasonId: string | null;
  scheduledStartTime: Date | null;
  initialEstimatedEndTime: Date;
  endTime: Date | null;
  notes: string | null;
  linkedCardId: string | null;
  createdById: string | null;
  endedById: string | null;
  secondaryReasonComment: string | null;
  parentOutOfServiceEventId: string | null;
  paipEventId: string | null;
  copiedFromId: string | null;
  alertWhenEventEnds: boolean;
  alertTimeInMinutes: number | null;
  outOfServiceTicketId: string | null;
  maintenanceTypes: Set<OosTicketMaintenanceType> | null;
  opportunityMXTypes: Set<OpportunityMXType> | null;
  qarReasons: List<IdListItemProps> | null;
  rtsDelayReasonTypes: Set<RtsDelayReasonType> | null;
  maintenanceLocation: string | null;
  mxDuringPeakTimeReason: string | null;
  assignedBaseId: string | null;
  amtPointOfContactId: string | null;
  alternatePOC: string | null;
  spareVehicleId: string | null;
  ticketDescription: string | null;
  humanErrorDamageType: HumanErrorDamageType | null;
  businessUnitCompanyId: string | null;
  isPlanned: boolean;
  isBirdStrike: boolean;
  isPatientOnboard: boolean;
  isAMTAvailable: boolean | null;
  isFAR135415Reportable: boolean | null;
  isFAR135417Reportable: boolean | null;
  waitingForParts: boolean;
  waitingForTooling: boolean;
  dateAcknowledged: Date | null;
  acknowledgedById: string | null;
  acknowledgementNotes: string | null;
  maintenanceFacilityId: string | null;
  locationId: string | null;
  address: AddressProps | null;
  crew: List<OutOfServiceCrewProps> | null;
  oosRequestType: OosRequestType | null;
  isAcknowledgementRequired: boolean;
  isEventAlreadyClosed: boolean | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class OutOfServiceEventRecord extends Record<OutOfServiceEventProps>({
  id: null as any,
  type: null as any,
  eventNumber: null as any,
  estimatedEndTime: null as any,
  startTime: null as any,
  isOpportunisticOOS: null as any,
  unitId: null as any,
  vehicleId: null as any,
  reasonId: null as any,
  isAirworthy: null as any,
  secondaryReasonId: null as any,
  scheduledStartTime: null as any,
  initialEstimatedEndTime: null as any,
  endTime: null as any,
  notes: null as any,
  linkedCardId: null as any,
  createdById: null as any,
  endedById: null as any,
  secondaryReasonComment: null as any,
  parentOutOfServiceEventId: null as any,
  paipEventId: null as any,
  copiedFromId: null as any,
  alertWhenEventEnds: null as any,
  alertTimeInMinutes: null as any,
  outOfServiceTicketId: null as any,
  maintenanceTypes: null as any,
  opportunityMXTypes: null as any,
  qarReasons: null as any,
  rtsDelayReasonTypes: null as any,
  maintenanceLocation: null as any,
  mxDuringPeakTimeReason: null as any,
  assignedBaseId: null as any,
  amtPointOfContactId: null as any,
  alternatePOC: null as any,
  spareVehicleId: null as any,
  ticketDescription: null as any,
  humanErrorDamageType: null as any,
  businessUnitCompanyId: null as any,
  isPlanned: null as any,
  isBirdStrike: null as any,
  isPatientOnboard: null as any,
  isAMTAvailable: null as any,
  isFAR135415Reportable: null as any,
  isFAR135417Reportable: null as any,
  waitingForParts: null as any,
  waitingForTooling: null as any,
  dateAcknowledged: null as any,
  acknowledgedById: null as any,
  acknowledgementNotes: null as any,
  maintenanceFacilityId: null as any,
  locationId: null as any,
  address: null as any,
  crew: null as any,
  oosRequestType: null as any,
  isAcknowledgementRequired: null as any,
  isEventAlreadyClosed: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    type: OutOfServiceType;
    eventNumber: string;
    estimatedEndTime: string | number | Date;
    startTime: string | number | Date | null;
    isOpportunisticOOS: boolean | null;
    unitId: string | null;
    vehicleId: string | null;
    reasonId: string;
    isAirworthy: boolean;
    secondaryReasonId: string | null;
    scheduledStartTime: string | number | Date | null;
    initialEstimatedEndTime: string | number | Date;
    endTime: string | number | Date | null;
    notes: string | null;
    linkedCardId: string | null;
    createdById: string | null;
    endedById: string | null;
    secondaryReasonComment: string | null;
    parentOutOfServiceEventId: string | null;
    paipEventId: string | null;
    copiedFromId: string | null;
    alertWhenEventEnds: boolean;
    alertTimeInMinutes: number | null;
    outOfServiceTicketId: string | null;
    maintenanceTypes: any;
    opportunityMXTypes: any;
    qarReasons: any;
    rtsDelayReasonTypes: any;
    maintenanceLocation: string | null;
    mxDuringPeakTimeReason: string | null;
    assignedBaseId: string | null;
    amtPointOfContactId: string | null;
    alternatePOC: string | null;
    spareVehicleId: string | null;
    ticketDescription: string | null;
    humanErrorDamageType: HumanErrorDamageType | null;
    businessUnitCompanyId: string | null;
    isPlanned: boolean;
    isBirdStrike: boolean;
    isPatientOnboard: boolean;
    isAMTAvailable: boolean | null;
    isFAR135415Reportable: boolean | null;
    isFAR135417Reportable: boolean | null;
    waitingForParts: boolean;
    waitingForTooling: boolean;
    dateAcknowledged: string | number | Date | null;
    acknowledgedById: string | null;
    acknowledgementNotes: string | null;
    maintenanceFacilityId: string | null;
    locationId: string | null;
    address: any;
    crew: any;
    oosRequestType: OosRequestType | null;
    isAcknowledgementRequired: boolean;
    isEventAlreadyClosed: boolean | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): OutOfServiceEventRecord {
    if (json == null) {
      throw new Error('OutOfServiceEventRecord.fromJS: parameter cannot be null');
    }
    return new OutOfServiceEventRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'OutOfServiceEventJson.id',
      ),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', OutOfServiceType.Unscheduled),
        'OutOfServiceEventJson.type',
      ),
      eventNumber: getFromOrThrow(json, (j) => j.eventNumber, 'OutOfServiceEventJson.eventNumber'),
      estimatedEndTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'estimatedEndTime', '0001-01-01T00:00:00.00Z'),
          'OutOfServiceEventJson.estimatedEndTime',
        ),
      ),
      startTime: json.startTime != null ? new Date(json.startTime) : null,
      isOpportunisticOOS: json.isOpportunisticOOS,
      unitId: json.unitId,
      vehicleId: json.vehicleId,
      reasonId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'reasonId', '00000000-0000-0000-0000-000000000000'),
        'OutOfServiceEventJson.reasonId',
      ),
      isAirworthy: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isAirworthy', false),
        'OutOfServiceEventJson.isAirworthy',
      ),
      secondaryReasonId: json.secondaryReasonId,
      scheduledStartTime:
        json.scheduledStartTime != null ? new Date(json.scheduledStartTime) : null,
      initialEstimatedEndTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'initialEstimatedEndTime', '0001-01-01T00:00:00.00Z'),
          'OutOfServiceEventJson.initialEstimatedEndTime',
        ),
      ),
      endTime: json.endTime != null ? new Date(json.endTime) : null,
      notes: json.notes,
      linkedCardId: json.linkedCardId,
      createdById: json.createdById,
      endedById: json.endedById,
      secondaryReasonComment: json.secondaryReasonComment,
      parentOutOfServiceEventId: json.parentOutOfServiceEventId,
      paipEventId: json.paipEventId,
      copiedFromId: json.copiedFromId,
      alertWhenEventEnds: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'alertWhenEventEnds', false),
        'OutOfServiceEventJson.alertWhenEventEnds',
      ),
      alertTimeInMinutes: json.alertTimeInMinutes,
      outOfServiceTicketId: json.outOfServiceTicketId,
      maintenanceTypes:
        json.maintenanceTypes != null ? Set<OosTicketMaintenanceType>(json.maintenanceTypes) : null,
      opportunityMXTypes:
        json.opportunityMXTypes != null ? Set<OpportunityMXType>(json.opportunityMXTypes) : null,
      qarReasons:
        json.qarReasons != null
          ? List<IdListItemRecord>(json.qarReasons.map((o: any) => new IdListItemRecord(o)))
          : null,
      rtsDelayReasonTypes:
        json.rtsDelayReasonTypes != null ? Set<RtsDelayReasonType>(json.rtsDelayReasonTypes) : null,
      maintenanceLocation: json.maintenanceLocation,
      mxDuringPeakTimeReason: json.mxDuringPeakTimeReason,
      assignedBaseId: json.assignedBaseId,
      amtPointOfContactId: json.amtPointOfContactId,
      alternatePOC: json.alternatePOC,
      spareVehicleId: json.spareVehicleId,
      ticketDescription: json.ticketDescription,
      humanErrorDamageType: json.humanErrorDamageType,
      businessUnitCompanyId: json.businessUnitCompanyId,
      isPlanned: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isPlanned', false),
        'OutOfServiceEventJson.isPlanned',
      ),
      isBirdStrike: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isBirdStrike', false),
        'OutOfServiceEventJson.isBirdStrike',
      ),
      isPatientOnboard: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isPatientOnboard', false),
        'OutOfServiceEventJson.isPatientOnboard',
      ),
      isAMTAvailable: json.isAMTAvailable,
      isFAR135415Reportable: json.isFAR135415Reportable,
      isFAR135417Reportable: json.isFAR135417Reportable,
      waitingForParts: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'waitingForParts', false),
        'OutOfServiceEventJson.waitingForParts',
      ),
      waitingForTooling: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'waitingForTooling', false),
        'OutOfServiceEventJson.waitingForTooling',
      ),
      dateAcknowledged: json.dateAcknowledged != null ? new Date(json.dateAcknowledged) : null,
      acknowledgedById: json.acknowledgedById,
      acknowledgementNotes: json.acknowledgementNotes,
      maintenanceFacilityId: json.maintenanceFacilityId,
      locationId: json.locationId,
      address: json.address != null ? new AddressRecord(json.address) : null,
      crew:
        json.crew != null
          ? List<OutOfServiceCrewRecord>(json.crew.map((o: any) => new OutOfServiceCrewRecord(o)))
          : null,
      oosRequestType: json.oosRequestType,
      isAcknowledgementRequired: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isAcknowledgementRequired', false),
        'OutOfServiceEventJson.isAcknowledgementRequired',
      ),
      isEventAlreadyClosed: json.isEventAlreadyClosed,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'OutOfServiceEventJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    type: OutOfServiceType;
    eventNumber: string;
    estimatedEndTime: string | number | Date;
    startTime: string | number | Date | null;
    isOpportunisticOOS: boolean | null;
    unitId: string | null;
    vehicleId: string | null;
    reasonId: string;
    isAirworthy: boolean;
    secondaryReasonId: string | null;
    scheduledStartTime: string | number | Date | null;
    initialEstimatedEndTime: string | number | Date;
    endTime: string | number | Date | null;
    notes: string | null;
    linkedCardId: string | null;
    createdById: string | null;
    endedById: string | null;
    secondaryReasonComment: string | null;
    parentOutOfServiceEventId: string | null;
    paipEventId: string | null;
    copiedFromId: string | null;
    alertWhenEventEnds: boolean;
    alertTimeInMinutes: number | null;
    outOfServiceTicketId: string | null;
    maintenanceTypes: any;
    opportunityMXTypes: any;
    qarReasons: any;
    rtsDelayReasonTypes: any;
    maintenanceLocation: string | null;
    mxDuringPeakTimeReason: string | null;
    assignedBaseId: string | null;
    amtPointOfContactId: string | null;
    alternatePOC: string | null;
    spareVehicleId: string | null;
    ticketDescription: string | null;
    humanErrorDamageType: HumanErrorDamageType | null;
    businessUnitCompanyId: string | null;
    isPlanned: boolean;
    isBirdStrike: boolean;
    isPatientOnboard: boolean;
    isAMTAvailable: boolean | null;
    isFAR135415Reportable: boolean | null;
    isFAR135417Reportable: boolean | null;
    waitingForParts: boolean;
    waitingForTooling: boolean;
    dateAcknowledged: string | number | Date | null;
    acknowledgedById: string | null;
    acknowledgementNotes: string | null;
    maintenanceFacilityId: string | null;
    locationId: string | null;
    address: any;
    crew: any;
    oosRequestType: OosRequestType | null;
    isAcknowledgementRequired: boolean;
    isEventAlreadyClosed: boolean | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [OutOfServiceEventRecord, Error[]] {
    if (json == null) {
      throw new Error('OutOfServiceEventRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new OutOfServiceEventRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'OutOfServiceEventJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', OutOfServiceType.Unscheduled),
            'OutOfServiceEventJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      eventNumber: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.eventNumber, 'OutOfServiceEventJson.eventNumber'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      estimatedEndTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'estimatedEndTime', '0001-01-01T00:00:00.00Z'),
              'OutOfServiceEventJson.estimatedEndTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      startTime: tryOrDefault(
        () => (json.startTime != null ? new Date(json.startTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isOpportunisticOOS: tryOrDefault(
        () => json.isOpportunisticOOS,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () => json.unitId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () => json.vehicleId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      reasonId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'reasonId', '00000000-0000-0000-0000-000000000000'),
            'OutOfServiceEventJson.reasonId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isAirworthy: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isAirworthy', false),
            'OutOfServiceEventJson.isAirworthy',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      secondaryReasonId: tryOrDefault(
        () => json.secondaryReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      scheduledStartTime: tryOrDefault(
        () => (json.scheduledStartTime != null ? new Date(json.scheduledStartTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      initialEstimatedEndTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'initialEstimatedEndTime', '0001-01-01T00:00:00.00Z'),
              'OutOfServiceEventJson.initialEstimatedEndTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      endTime: tryOrDefault(
        () => (json.endTime != null ? new Date(json.endTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      linkedCardId: tryOrDefault(
        () => json.linkedCardId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      createdById: tryOrDefault(
        () => json.createdById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      endedById: tryOrDefault(
        () => json.endedById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      secondaryReasonComment: tryOrDefault(
        () => json.secondaryReasonComment,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      parentOutOfServiceEventId: tryOrDefault(
        () => json.parentOutOfServiceEventId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      paipEventId: tryOrDefault(
        () => json.paipEventId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      copiedFromId: tryOrDefault(
        () => json.copiedFromId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      alertWhenEventEnds: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'alertWhenEventEnds', false),
            'OutOfServiceEventJson.alertWhenEventEnds',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      alertTimeInMinutes: tryOrDefault(
        () => json.alertTimeInMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      outOfServiceTicketId: tryOrDefault(
        () => json.outOfServiceTicketId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      maintenanceTypes: tryOrDefault(
        () =>
          json.maintenanceTypes != null
            ? Set<OosTicketMaintenanceType>(json.maintenanceTypes)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      opportunityMXTypes: tryOrDefault(
        () =>
          json.opportunityMXTypes != null ? Set<OpportunityMXType>(json.opportunityMXTypes) : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      qarReasons: tryOrDefault(
        () =>
          json.qarReasons != null
            ? List<IdListItemRecord>(json.qarReasons.map((o: any) => new IdListItemRecord(o)))
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rtsDelayReasonTypes: tryOrDefault(
        () =>
          json.rtsDelayReasonTypes != null
            ? Set<RtsDelayReasonType>(json.rtsDelayReasonTypes)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      maintenanceLocation: tryOrDefault(
        () => json.maintenanceLocation,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      mxDuringPeakTimeReason: tryOrDefault(
        () => json.mxDuringPeakTimeReason,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      assignedBaseId: tryOrDefault(
        () => json.assignedBaseId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      amtPointOfContactId: tryOrDefault(
        () => json.amtPointOfContactId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      alternatePOC: tryOrDefault(
        () => json.alternatePOC,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      spareVehicleId: tryOrDefault(
        () => json.spareVehicleId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ticketDescription: tryOrDefault(
        () => json.ticketDescription,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      humanErrorDamageType: tryOrDefault(
        () => json.humanErrorDamageType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      businessUnitCompanyId: tryOrDefault(
        () => json.businessUnitCompanyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isPlanned: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isPlanned', false),
            'OutOfServiceEventJson.isPlanned',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isBirdStrike: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isBirdStrike', false),
            'OutOfServiceEventJson.isBirdStrike',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isPatientOnboard: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isPatientOnboard', false),
            'OutOfServiceEventJson.isPatientOnboard',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isAMTAvailable: tryOrDefault(
        () => json.isAMTAvailable,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isFAR135415Reportable: tryOrDefault(
        () => json.isFAR135415Reportable,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isFAR135417Reportable: tryOrDefault(
        () => json.isFAR135417Reportable,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      waitingForParts: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'waitingForParts', false),
            'OutOfServiceEventJson.waitingForParts',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      waitingForTooling: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'waitingForTooling', false),
            'OutOfServiceEventJson.waitingForTooling',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateAcknowledged: tryOrDefault(
        () => (json.dateAcknowledged != null ? new Date(json.dateAcknowledged) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      acknowledgedById: tryOrDefault(
        () => json.acknowledgedById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      acknowledgementNotes: tryOrDefault(
        () => json.acknowledgementNotes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      maintenanceFacilityId: tryOrDefault(
        () => json.maintenanceFacilityId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationId: tryOrDefault(
        () => json.locationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      address: tryOrDefault(
        () => (json.address != null ? new AddressRecord(json.address) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      crew: tryOrDefault(
        () =>
          json.crew != null
            ? List<OutOfServiceCrewRecord>(json.crew.map((o: any) => new OutOfServiceCrewRecord(o)))
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      oosRequestType: tryOrDefault(
        () => json.oosRequestType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isAcknowledgementRequired: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isAcknowledgementRequired', false),
            'OutOfServiceEventJson.isAcknowledgementRequired',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isEventAlreadyClosed: tryOrDefault(
        () => json.isEventAlreadyClosed,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'OutOfServiceEventJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: OutOfServiceEventProps) {
    super(props);
    if (props == null) {
      throw new Error('OutOfServiceEventRecord constructor parameter cannot be null');
    }
  }
}

export abstract class OutOfServiceEventViewModelBase implements OutOfServiceEventProps {
  protected readonly _props: OutOfServiceEventProps;
  private _json?: OutOfServiceEventProps;

  get id(): string {
    return this._props.id;
  }
  get type(): OutOfServiceType {
    return this._props.type;
  }
  get eventNumber(): string {
    return this._props.eventNumber;
  }
  get estimatedEndTime(): Date {
    return this._props.estimatedEndTime;
  }
  get startTime(): Date | null {
    return this._props.startTime;
  }
  get isOpportunisticOOS(): boolean | null {
    return this._props.isOpportunisticOOS;
  }
  get unitId(): string | null {
    return this._props.unitId;
  }
  get vehicleId(): string | null {
    return this._props.vehicleId;
  }
  get reasonId(): string {
    return this._props.reasonId;
  }
  get isAirworthy(): boolean {
    return this._props.isAirworthy;
  }
  get secondaryReasonId(): string | null {
    return this._props.secondaryReasonId;
  }
  get scheduledStartTime(): Date | null {
    return this._props.scheduledStartTime;
  }
  get initialEstimatedEndTime(): Date {
    return this._props.initialEstimatedEndTime;
  }
  get endTime(): Date | null {
    return this._props.endTime;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get linkedCardId(): string | null {
    return this._props.linkedCardId;
  }
  get createdById(): string | null {
    return this._props.createdById;
  }
  get endedById(): string | null {
    return this._props.endedById;
  }
  get secondaryReasonComment(): string | null {
    return this._props.secondaryReasonComment;
  }
  get parentOutOfServiceEventId(): string | null {
    return this._props.parentOutOfServiceEventId;
  }
  get paipEventId(): string | null {
    return this._props.paipEventId;
  }
  get copiedFromId(): string | null {
    return this._props.copiedFromId;
  }
  get alertWhenEventEnds(): boolean {
    return this._props.alertWhenEventEnds;
  }
  get alertTimeInMinutes(): number | null {
    return this._props.alertTimeInMinutes;
  }
  get outOfServiceTicketId(): string | null {
    return this._props.outOfServiceTicketId;
  }
  get maintenanceTypes(): Set<OosTicketMaintenanceType> | null {
    return this._props.maintenanceTypes;
  }
  get opportunityMXTypes(): Set<OpportunityMXType> | null {
    return this._props.opportunityMXTypes;
  }
  get qarReasons(): List<IdListItemProps> | null {
    return this._props.qarReasons;
  }
  get rtsDelayReasonTypes(): Set<RtsDelayReasonType> | null {
    return this._props.rtsDelayReasonTypes;
  }
  get maintenanceLocation(): string | null {
    return this._props.maintenanceLocation;
  }
  get mxDuringPeakTimeReason(): string | null {
    return this._props.mxDuringPeakTimeReason;
  }
  get assignedBaseId(): string | null {
    return this._props.assignedBaseId;
  }
  get amtPointOfContactId(): string | null {
    return this._props.amtPointOfContactId;
  }
  get alternatePOC(): string | null {
    return this._props.alternatePOC;
  }
  get spareVehicleId(): string | null {
    return this._props.spareVehicleId;
  }
  get ticketDescription(): string | null {
    return this._props.ticketDescription;
  }
  get humanErrorDamageType(): HumanErrorDamageType | null {
    return this._props.humanErrorDamageType;
  }
  get businessUnitCompanyId(): string | null {
    return this._props.businessUnitCompanyId;
  }
  get isPlanned(): boolean {
    return this._props.isPlanned;
  }
  get isBirdStrike(): boolean {
    return this._props.isBirdStrike;
  }
  get isPatientOnboard(): boolean {
    return this._props.isPatientOnboard;
  }
  get isAMTAvailable(): boolean | null {
    return this._props.isAMTAvailable;
  }
  get isFAR135415Reportable(): boolean | null {
    return this._props.isFAR135415Reportable;
  }
  get isFAR135417Reportable(): boolean | null {
    return this._props.isFAR135417Reportable;
  }
  get waitingForParts(): boolean {
    return this._props.waitingForParts;
  }
  get waitingForTooling(): boolean {
    return this._props.waitingForTooling;
  }
  get dateAcknowledged(): Date | null {
    return this._props.dateAcknowledged;
  }
  get acknowledgedById(): string | null {
    return this._props.acknowledgedById;
  }
  get acknowledgementNotes(): string | null {
    return this._props.acknowledgementNotes;
  }
  get maintenanceFacilityId(): string | null {
    return this._props.maintenanceFacilityId;
  }
  get locationId(): string | null {
    return this._props.locationId;
  }
  get address(): AddressProps | null {
    return this._props.address;
  }
  get crew(): List<OutOfServiceCrewProps> | null {
    return this._props.crew;
  }
  get oosRequestType(): OosRequestType | null {
    return this._props.oosRequestType;
  }
  get isAcknowledgementRequired(): boolean {
    return this._props.isAcknowledgementRequired;
  }
  get isEventAlreadyClosed(): boolean | null {
    return this._props.isEventAlreadyClosed;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isOpportunistic() {
    return this._props.type === OutOfServiceType.Opportunistic;
  }
  public get isRefurbishmentOrRefresh() {
    return this._props.type === OutOfServiceType.RefurbishmentOrRefresh;
  }
  public get isScheduled() {
    return this._props.type === OutOfServiceType.Scheduled;
  }
  public get isUnscheduled() {
    return this._props.type === OutOfServiceType.Unscheduled;
  }
  public get isMaintenance() {
    return this._props.humanErrorDamageType === HumanErrorDamageType.Maintenance;
  }
  public get isMedicalCrew() {
    return this._props.humanErrorDamageType === HumanErrorDamageType.MedicalCrew;
  }
  public get isPilot() {
    return this._props.humanErrorDamageType === HumanErrorDamageType.Pilot;
  }
  public get isOosRum() {
    return this._props.oosRequestType === OosRequestType.OosRum;
  }
  public get isOps() {
    return this._props.oosRequestType === OosRequestType.Ops;
  }

  public isAnyType(...outOfServiceType: OutOfServiceType[]) {
    return outOfServiceType.includes(this._props.type);
  }
  public isAnyHumanErrorDamageType(...humanErrorDamageType: HumanErrorDamageType[]) {
    return (
      this._props.humanErrorDamageType != null &&
      humanErrorDamageType.includes(this._props.humanErrorDamageType)
    );
  }
  public isAnyOosRequestType(...oosRequestType: OosRequestType[]) {
    return (
      this._props.oosRequestType != null && oosRequestType.includes(this._props.oosRequestType)
    );
  }

  constructor(props: OutOfServiceEventProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        type: this._props.type,
        eventNumber: this._props.eventNumber,
        estimatedEndTime: this._props.estimatedEndTime,
        startTime: this._props.startTime,
        isOpportunisticOOS: this._props.isOpportunisticOOS,
        unitId: this._props.unitId,
        vehicleId: this._props.vehicleId,
        reasonId: this._props.reasonId,
        isAirworthy: this._props.isAirworthy,
        secondaryReasonId: this._props.secondaryReasonId,
        scheduledStartTime: this._props.scheduledStartTime,
        initialEstimatedEndTime: this._props.initialEstimatedEndTime,
        endTime: this._props.endTime,
        notes: this._props.notes,
        linkedCardId: this._props.linkedCardId,
        createdById: this._props.createdById,
        endedById: this._props.endedById,
        secondaryReasonComment: this._props.secondaryReasonComment,
        parentOutOfServiceEventId: this._props.parentOutOfServiceEventId,
        paipEventId: this._props.paipEventId,
        copiedFromId: this._props.copiedFromId,
        alertWhenEventEnds: this._props.alertWhenEventEnds,
        alertTimeInMinutes: this._props.alertTimeInMinutes,
        outOfServiceTicketId: this._props.outOfServiceTicketId,
        maintenanceTypes: this._props.maintenanceTypes,
        opportunityMXTypes: this._props.opportunityMXTypes,
        qarReasons: this._props.qarReasons,
        rtsDelayReasonTypes: this._props.rtsDelayReasonTypes,
        maintenanceLocation: this._props.maintenanceLocation,
        mxDuringPeakTimeReason: this._props.mxDuringPeakTimeReason,
        assignedBaseId: this._props.assignedBaseId,
        amtPointOfContactId: this._props.amtPointOfContactId,
        alternatePOC: this._props.alternatePOC,
        spareVehicleId: this._props.spareVehicleId,
        ticketDescription: this._props.ticketDescription,
        humanErrorDamageType: this._props.humanErrorDamageType,
        businessUnitCompanyId: this._props.businessUnitCompanyId,
        isPlanned: this._props.isPlanned,
        isBirdStrike: this._props.isBirdStrike,
        isPatientOnboard: this._props.isPatientOnboard,
        isAMTAvailable: this._props.isAMTAvailable,
        isFAR135415Reportable: this._props.isFAR135415Reportable,
        isFAR135417Reportable: this._props.isFAR135417Reportable,
        waitingForParts: this._props.waitingForParts,
        waitingForTooling: this._props.waitingForTooling,
        dateAcknowledged: this._props.dateAcknowledged,
        acknowledgedById: this._props.acknowledgedById,
        acknowledgementNotes: this._props.acknowledgementNotes,
        maintenanceFacilityId: this._props.maintenanceFacilityId,
        locationId: this._props.locationId,
        address: this._props.address,
        crew: this._props.crew,
        oosRequestType: this._props.oosRequestType,
        isAcknowledgementRequired: this._props.isAcknowledgementRequired,
        isEventAlreadyClosed: this._props.isEventAlreadyClosed,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllOutOfServiceEventEntities = (state: AppState) =>
  state.entityStores.outOfServiceEvent.entities;

export const selectOutOfServiceEventById = (id: string) => (state: AppState) =>
  state.entityStores.outOfServiceEvent.entities.get(id);

export const selectOutOfServiceEventMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.outOfServiceEvent.meta.get(id);

export const selectOutOfServiceEventAndMetadataById = (id: string) =>
  createSelector(
    selectOutOfServiceEventById(id),
    selectOutOfServiceEventMetadataById(id),
    (outOfServiceEvent, meta) => ({ outOfServiceEvent, meta }),
  );

export function selectOutOfServiceEventEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllOutOfServiceEventEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, OutOfServiceEventRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectOutOfServiceEventEntitiesByType(type: OutOfServiceType) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByEventNumber(eventNumber: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.eventNumber === eventNumber).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByEstimatedEndTime(estimatedEndTime: Date) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.estimatedEndTime === estimatedEndTime).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByStartTime(startTime: Date) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.startTime === startTime).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByIsOpportunisticOOS(isOpportunisticOOS: boolean) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.isOpportunisticOOS === isOpportunisticOOS).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByReasonId(reasonId: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.reasonId === reasonId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByIsAirworthy(isAirworthy: boolean) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.isAirworthy === isAirworthy).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesBySecondaryReasonId(secondaryReasonId: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.secondaryReasonId === secondaryReasonId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByScheduledStartTime(scheduledStartTime: Date) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.scheduledStartTime === scheduledStartTime).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByInitialEstimatedEndTime(
  initialEstimatedEndTime: Date,
) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.initialEstimatedEndTime === initialEstimatedEndTime).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByEndTime(endTime: Date) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.endTime === endTime).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByNotes(notes: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByLinkedCardId(linkedCardId: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.linkedCardId === linkedCardId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByCreatedById(createdById: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.createdById === createdById).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByEndedById(endedById: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.endedById === endedById).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesBySecondaryReasonComment(
  secondaryReasonComment: string,
) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.secondaryReasonComment === secondaryReasonComment).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByParentOutOfServiceEventId(
  parentOutOfServiceEventId: string,
) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.parentOutOfServiceEventId === parentOutOfServiceEventId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByPaipEventId(paipEventId: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.paipEventId === paipEventId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByCopiedFromId(copiedFromId: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.copiedFromId === copiedFromId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByAlertWhenEventEnds(alertWhenEventEnds: boolean) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.alertWhenEventEnds === alertWhenEventEnds).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByAlertTimeInMinutes(alertTimeInMinutes: number) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.alertTimeInMinutes === alertTimeInMinutes).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByOutOfServiceTicketId(
  outOfServiceTicketId: string,
) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.outOfServiceTicketId === outOfServiceTicketId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByMaintenanceLocation(maintenanceLocation: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.maintenanceLocation === maintenanceLocation).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByMxDuringPeakTimeReason(
  mxDuringPeakTimeReason: string,
) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.mxDuringPeakTimeReason === mxDuringPeakTimeReason).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByAssignedBaseId(assignedBaseId: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.assignedBaseId === assignedBaseId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByAmtPointOfContactId(amtPointOfContactId: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.amtPointOfContactId === amtPointOfContactId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByAlternatePOC(alternatePOC: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.alternatePOC === alternatePOC).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesBySpareVehicleId(spareVehicleId: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.spareVehicleId === spareVehicleId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByTicketDescription(ticketDescription: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.ticketDescription === ticketDescription).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByHumanErrorDamageType(
  humanErrorDamageType: HumanErrorDamageType,
) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.humanErrorDamageType === humanErrorDamageType).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByBusinessUnitCompanyId(
  businessUnitCompanyId: string,
) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.businessUnitCompanyId === businessUnitCompanyId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByIsPlanned(isPlanned: boolean) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.isPlanned === isPlanned).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByIsBirdStrike(isBirdStrike: boolean) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.isBirdStrike === isBirdStrike).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByIsPatientOnboard(isPatientOnboard: boolean) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.isPatientOnboard === isPatientOnboard).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByIsAMTAvailable(isAMTAvailable: boolean) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.isAMTAvailable === isAMTAvailable).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByIsFAR135415Reportable(
  isFAR135415Reportable: boolean,
) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.isFAR135415Reportable === isFAR135415Reportable).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByIsFAR135417Reportable(
  isFAR135417Reportable: boolean,
) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.isFAR135417Reportable === isFAR135417Reportable).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByWaitingForParts(waitingForParts: boolean) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.waitingForParts === waitingForParts).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByWaitingForTooling(waitingForTooling: boolean) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.waitingForTooling === waitingForTooling).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByDateAcknowledged(dateAcknowledged: Date) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.dateAcknowledged === dateAcknowledged).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByAcknowledgedById(acknowledgedById: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.acknowledgedById === acknowledgedById).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByAcknowledgementNotes(
  acknowledgementNotes: string,
) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.acknowledgementNotes === acknowledgementNotes).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByMaintenanceFacilityId(
  maintenanceFacilityId: string,
) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.maintenanceFacilityId === maintenanceFacilityId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByLocationId(locationId: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.locationId === locationId).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByOosRequestType(oosRequestType: OosRequestType) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.oosRequestType === oosRequestType).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByIsAcknowledgementRequired(
  isAcknowledgementRequired: boolean,
) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.isAcknowledgementRequired === isAcknowledgementRequired).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByIsEventAlreadyClosed(
  isEventAlreadyClosed: boolean,
) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.isEventAlreadyClosed === isEventAlreadyClosed).valueSeq(),
  );
}

export function selectOutOfServiceEventEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllOutOfServiceEventEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
